import React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Step {
  label: string
  description?: string
}

interface OnboardingStepperProps {
  /** 1-based. Pass steps.length + 1 to render every station completed (Finish page). */
  currentStep: number
  steps: Step[]
  /**
   * When provided, COMPLETED steps become real buttons (keyboard-focusable,
   * Enter/Space) that call back with the 1-based step number. Steps ahead of the
   * operator are never interactive — the journey only walks forward.
   */
  onStepSelect?: (stepNumber: number) => void
}

/**
 * The persistent journey rail for the onboarding shell: Connect → Choose property
 * → Review → Finish. Semantically an ordered list inside a labelled <nav>;
 * the current station carries aria-current="step".
 */
export default function OnboardingStepper({ currentStep, steps, onStepSelect }: OnboardingStepperProps) {
  return (
    <nav aria-label="Setup progress">
      {/* Desktop rail */}
      <ol className="hidden items-start sm:flex">
        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isCompleted = stepNumber < currentStep
          const isCurrent = stepNumber === currentStep
          const isFuture = stepNumber > currentStep
          const isNavigable = isCompleted && !!onStepSelect

          const circle = (
            <span
              aria-hidden="true"
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-500',
                isCompleted && 'border-emerald-500 bg-emerald-500 text-white',
                isCurrent && 'border-primary bg-primary text-primary-foreground shadow-md shadow-primary/25',
                isFuture && 'border-border bg-white text-muted-foreground',
                isNavigable && 'group-hover:border-emerald-600 group-hover:bg-emerald-600'
              )}
              style={isCurrent ? { animation: 'stepPulse 2s ease-in-out infinite' } : undefined}
            >
              {isCompleted ? (
                <Check className="h-4 w-4" style={{ animation: 'checkReveal 0.3s ease-out' }} />
              ) : (
                stepNumber
              )}
            </span>
          )

          const labels = (
            <>
              <span
                className={cn(
                  'mt-2 text-xs font-medium transition-colors duration-300',
                  isCompleted && 'text-emerald-600',
                  isCurrent && 'text-foreground',
                  isFuture && 'text-muted-foreground',
                  isNavigable && 'group-hover:text-emerald-700'
                )}
              >
                {step.label}
              </span>
              {step.description && (
                <span className="mt-0.5 text-[10px] text-muted-foreground">{step.description}</span>
              )}
            </>
          )

          return (
            <React.Fragment key={stepNumber}>
              <li
                className="flex flex-col items-center"
                style={{ flex: '0 0 auto', minWidth: 92 }}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {isNavigable ? (
                  <button
                    type="button"
                    onClick={() => onStepSelect?.(stepNumber)}
                    className="group flex flex-col items-center rounded-lg px-2 py-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    aria-label={`Go back to step ${stepNumber}: ${step.label}`}
                  >
                    {circle}
                    {labels}
                  </button>
                ) : (
                  <span className="flex flex-col items-center px-2 py-1">
                    {circle}
                    <span className="sr-only">
                      {isCompleted ? 'Completed step' : isCurrent ? 'Current step' : 'Upcoming step'}:
                    </span>
                    {labels}
                  </span>
                )}
              </li>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <li aria-hidden="true" className="mt-[22px] flex flex-1 items-center px-1">
                  <span className="relative block h-0.5 w-full overflow-hidden rounded-full bg-border">
                    <span
                      className="absolute inset-y-0 left-0 rounded-full bg-emerald-500 transition-all duration-700 ease-out"
                      style={{ width: isCompleted ? '100%' : '0%' }}
                    />
                  </span>
                </li>
              )}
            </React.Fragment>
          )
        })}
      </ol>

      {/* Mobile rail — compact dots with the current station named */}
      <div className="sm:hidden">
        <ol className="flex items-center justify-center gap-2">
          {steps.map((step, index) => {
            const stepNumber = index + 1
            const isCompleted = stepNumber < currentStep
            const isCurrent = stepNumber === currentStep
            const isNavigable = isCompleted && !!onStepSelect

            const dotClass = cn(
              'block h-2 rounded-full transition-all duration-500',
              isCompleted && 'w-2 bg-emerald-500',
              isCurrent && 'w-8 bg-primary',
              stepNumber > currentStep && 'w-2 bg-border'
            )

            return (
              <li key={stepNumber} aria-current={isCurrent ? 'step' : undefined}>
                {isNavigable ? (
                  <button
                    type="button"
                    onClick={() => onStepSelect?.(stepNumber)}
                    aria-label={`Go back to step ${stepNumber}: ${step.label}`}
                    className="flex h-6 items-center rounded-full px-0.5 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    <span className={dotClass} />
                  </button>
                ) : (
                  <span className="flex h-6 items-center px-0.5" aria-hidden="true">
                    <span className={dotClass} />
                  </span>
                )}
              </li>
            )
          })}
        </ol>
        {currentStep <= steps.length ? (
          <>
            <p className="mt-3 text-center text-sm font-medium text-foreground">
              Step {currentStep} of {steps.length}: {steps[currentStep - 1]?.label}
            </p>
            {steps[currentStep - 1]?.description && (
              <p className="mt-0.5 text-center text-xs text-muted-foreground">
                {steps[currentStep - 1].description}
              </p>
            )}
          </>
        ) : (
          <p className="mt-3 text-center text-sm font-medium text-emerald-600">Setup complete</p>
        )}
      </div>

      {/* Inline animations */}
      <style>{`
        @keyframes stepPulse {
          0%, 100% { box-shadow: 0 4px 6px -1px rgba(13, 148, 136, 0.15); }
          50% { box-shadow: 0 4px 12px -1px rgba(13, 148, 136, 0.35); }
        }
        @keyframes checkReveal {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </nav>
  )
}
